import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, Pencil, X } from 'lucide-react';
import { useModal } from '../../hooks/modalContext';
import { useProfileImage } from '../../hooks/profilePhoto';
import user from '../../../assets/helpers/user.png';
import DateCalendar from '../../UI/DateCalendar/DateCalendar';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useAuth } from '../../../auth/Auth';
import {
  getTenantDetails,
  getUserByToken,
  getTenantCompany,
  updatePersonalInfo,
  addCompanyInformation,
  getKYCBusinessTypes,
  getStakeholders,
  addStakeholder,
  deleteStakeholder,
  updateStakeholder,
} from '../../../auth/_requests';
import {
  formatDateToDisplayUTC,
  formatPhoneNumber,
  parseDateStringToDate,
  standardizeDate,
} from '../../utils/utils';
import { tenantDetails, TenantCompany, Stakeholder } from '../../utils/types';
import { occupationSchema } from '../../utils/occupationOptions';
import { showToast } from '../../utils/showToast';
import UploadPhoto from '../UploadPhoto/UploadPhoto';

import UpdatePhone from '../UpdatePhone/UpdatePhone';
import VerifyPhoneCode from '../UpdatePhone/VerifyPhoneCode';
import { useUserProfile } from '../../hooks/userProfile';

const Profile = () => {
  const { setModal } = useModal();
  const { userProfile, setUserProfile } = useUserProfile();
  const { auth, currentUser, setCurrentUser } = useAuth();
  const { profileImageUrl } = useProfileImage();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [userData, setUserData] = useState<tenantDetails>();
  const [companyDetails, setCompanyDetails] = useState<TenantCompany | null>(null);
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [isEditingStakeholders, setIsEditingStakeholders] = useState(false);
  const [editingStakeholderId, setEditingStakeholderId] = useState<string | null>(null);
  const [kycBusinessTypes, setKycBusinessTypes] = useState<
    { kycBusinessTypeId: string; value: string }[]
  >([]);
  const [currentview, setCurrentView] = useState<
    'profile' | 'upload-photo' | 'update-phone' | 'verify-phone-code'
  >('profile');
  const getView = (view: string) => {
    switch (view) {
      case 'profile':
        return (
          <div className='flex flex-col items-center h-full  px-12'>
            <div className='relative w-full flex justify-center items-center'>
              {/* Shadow Element */}
              <div className='absolute -bottom-0 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#0055FF] z-0 pointer-events-none'></div>

              {/* Card */}
              <div className='relative z-10 w-full rounded-[20px] border border-[#698AF5] bg-[linear-gradient(229deg,_#0055FF_-13.57%,_#003BB5_113.63%)]'>
                <div className='p-7 flex flex-col items-center gap-4'>
                  <div className='flex flex-col items-center gap-3'>
                    <div
                      className='w-[72px] h-[72px] rounded-full bg-cover bg-center shadow-inner'
                      style={{
                        backgroundImage: `url(${profileImageUrl || user})`,
                        boxShadow: 'inset 0px 4px 4px rgba(255, 255, 255, 0.25)',
                      }}
                    />
                  </div>

                  <div className='flex gap-2 items-center justify-center'>
                    <Pencil
                      className='h-4 w-4 text-white cursor-pointer'
                      onClick={() => setCurrentView('upload-photo')}
                    />
                    <span className='text-white text-sm font-semibold'>Edit</span>
                  </div>

                  <div className='flex flex-col items-center gap-1'>
                    <span className='text-white text-base font-semibold'>
                      {userProfile.firstName} {userProfile.lastName}
                    </span>
                    <span className='text-white/70 text-sm '>{currentUser?.user?.email}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className='flex flex-col items-center justify-center gap-10 mt-10'>
              <div className='flex flex-col [@media(min-width:400px)]:flex-row items-start gap-8'>
                <div className={'inputWrapper'}>
                  <div className={'label-light'}>First name</div>
                  <div
                    className={'no-border-input-field'}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  >
                    <input
                      name='firstname'
                      placeholder='--'
                      autoComplete='off'
                      value={formik.values.firstname}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={true}
                      className='text-light/60'
                    />
                  </div>
                  {formik.touched.firstname && formik.errors.firstname && (
                    <div className='error'>{formik.errors.firstname}</div>
                  )}
                </div>
                <div className={'inputWrapper'}>
                  <div className={'label-light'}>Last name</div>
                  <div
                    className={'no-border-input-field'}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  >
                    <input
                      name='lastname'
                      placeholder='--'
                      autoComplete='off'
                      value={formik.values.lastname}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={true}
                      className='text-light/60'
                    />
                  </div>
                  {formik.touched.lastname && formik.errors.lastname && (
                    <div className='error'>{formik.errors.lastname}</div>
                  )}
                </div>
              </div>
              <div className='w-full'>
                <div className={'inputWrapper'}>
                  <div className={'label-light'}>Middle name (optional)</div>
                  <div
                    className={'no-border-input-field'}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  >
                    <input
                      name='middlename'
                      placeholder='--'
                      autoComplete='off'
                      value={formik.values.middlename}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={true}
                      className='text-light/60'
                    />
                  </div>
                  {formik.touched.middlename && formik.errors.middlename && (
                    <div className='error'>{formik.errors.middlename}</div>
                  )}
                </div>
              </div>
              <div className='w-full'>
                <div className={'inputWrapper'}>
                  <div className={'label'}>Phone number</div>
                  <div
                    className={'inputField flex justify-between'}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  >
                    <input
                      placeholder={'+1 (XXX) XXX-XXXX'}
                      autoComplete='off'
                      disabled={true}
                      value={formatPhoneNumber(formik.values.phoneNumber)}
                      readOnly={true}
                    />
                    <Pencil
                      className='h-5 w-5 text-primary cursor-pointer'
                      onClick={() => setCurrentView('update-phone')}
                    />
                  </div>
                </div>
                <p className='text-xs text-light/70 mt-2'>
                  Your phone number will be used for{' '}
                  <span className='font-semibold'>multi-factor authentication (MFA) </span>
                  and to receive{' '}
                  <span className='font-semibold'>
                    important account updates and promotional messages
                  </span>
                  .
                </p>
              </div>
              <div className='w-full'>
                <div className={'inputWrapper'}>
                  <div className={'label-light'}>Date of birth</div>
                  <div
                    style={{
                      backgroundColor: 'rgba(236, 238, 243, 0.4)',
                    }}
                    className='outline-none !rounded-[8px] h-[52px]'
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  >
                    <DateCalendar
                      startDate={startDate}
                      onDateChange={setStartDate}
                      formik={formik}
                      type='dob'
                      maxDate={new Date()}
                      maxYear={1}
                      disable={true}
                    />
                  </div>
                  {formik.touched.dob && formik.errors.dob && (
                    <div className='error'>{formik.errors.dob}</div>
                  )}
                </div>
              </div>
              <div className='w-full'>
                <div className={'inputWrapper'}>
                  <div className={'label-light'}>Occupation</div>
                  <div
                    className={'no-border-input-field'}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  >
                    <input
                      name='occupation'
                      placeholder='--'
                      autoComplete='off'
                      value={formik.values.occupation}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={true}
                      className='text-light/60'
                    />
                  </div>
                  {formik.touched.occupation && formik.errors.occupation && (
                    <div className='error'>{formik.errors.occupation}</div>
                  )}
                </div>
              </div>
              {companyDetails && (
                <div className='w-full'>
                  <div className='flex items-center justify-between mb-4'>
                    <div className={'label-light'}>Company information</div>
                    <Pencil
                      className='h-4 w-4 text-primary cursor-pointer'
                      onClick={() => setIsEditingCompany(!isEditingCompany)}
                    />
                  </div>
                  {!isEditingCompany ? (
                    <div className='flex flex-col gap-3 p-4 bg-gray-50 rounded-lg'>
                      <div>
                        <span className='text-xs text-light/60'>Company legal name:</span>
                        <p className='text-sm text-light'>{companyDetails.name || '--'}</p>
                      </div>
                      {companyDetails.displayName && (
                        <div>
                          <span className='text-xs text-light/60'>Business name:</span>
                          <p className='text-sm text-light'>{companyDetails.displayName}</p>
                        </div>
                      )}
                      {companyDetails.kycBusinessTypeValue && (
                        <div>
                          <span className='text-xs text-light/60'>Business type:</span>
                          <p className='text-sm text-light'>
                            {companyDetails.kycBusinessTypeValue}
                          </p>
                        </div>
                      )}
                      {companyDetails.documentUrl && (
                        <div>
                          <span className='text-xs text-light/60'>Document:</span>
                          <a
                            href={companyDetails.documentUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-sm text-primary hover:underline'
                          >
                            View document
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className='flex flex-col gap-4 p-4 bg-gray-50 rounded-lg'>
                      <div className={'inputWrapper'}>
                        <div className={'label-light'}>Company legal name</div>
                        <div className={'inputField'}>
                          <input
                            name='companyName'
                            placeholder='Enter Company Legal Name'
                            autoComplete='off'
                            value={companyFormik.values.companyName}
                            onChange={companyFormik.handleChange}
                            onBlur={companyFormik.handleBlur}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                              }
                            }}
                          />
                        </div>
                        {companyFormik.touched.companyName && companyFormik.errors.companyName && (
                          <div className='error'>{companyFormik.errors.companyName}</div>
                        )}
                      </div>
                      <div className={'inputWrapper'}>
                        <div className={'label-light'}>Business name (optional)</div>
                        <div className={'inputField'}>
                          <input
                            name='companyDisplayName'
                            placeholder='Enter Business Name'
                            autoComplete='off'
                            value={companyFormik.values.companyDisplayName}
                            onChange={companyFormik.handleChange}
                            onBlur={companyFormik.handleBlur}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                              }
                            }}
                          />
                        </div>
                        {companyFormik.touched.companyDisplayName &&
                          companyFormik.errors.companyDisplayName && (
                            <div className='error'>{companyFormik.errors.companyDisplayName}</div>
                          )}
                      </div>
                      <div className={'inputWrapper'}>
                        <div className={'label-light'}>Business type</div>
                        <div className={'inputField'}>
                          <select
                            name='companyKycBusinessTypeId'
                            value={companyFormik.values.companyKycBusinessTypeId}
                            onChange={companyFormik.handleChange}
                            onBlur={companyFormik.handleBlur}
                            className='w-full bg-transparent outline-none'
                          >
                            <option value='' disabled>
                              Select business type
                            </option>
                            {kycBusinessTypes?.map((type) => (
                              <option key={type.kycBusinessTypeId} value={type.kycBusinessTypeId}>
                                {type.value}
                              </option>
                            ))}
                          </select>
                        </div>
                        {companyFormik.touched.companyKycBusinessTypeId &&
                          companyFormik.errors.companyKycBusinessTypeId && (
                            <div className='error'>
                              {companyFormik.errors.companyKycBusinessTypeId}
                            </div>
                          )}
                      </div>
                      <div className='flex gap-2'>
                        <button
                          type='button'
                          className='flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100'
                          onClick={() => {
                            setIsEditingCompany(false);
                            companyFormik.resetForm();
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type='button'
                          className='flex-1 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50'
                          onClick={() => companyFormik.handleSubmit()}
                          disabled={companyFormik.isSubmitting || !companyFormik.isValid}
                        >
                          {companyFormik.isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {companyDetails && (
                <div className='w-full'>
                  <div className='flex items-center justify-between mb-4'>
                    <div className={'label-light'}>Stakeholders</div>
                    {stakeholders.length > 0 && (
                      <Pencil
                        className='h-4 w-4 text-primary cursor-pointer'
                        onClick={() => setIsEditingStakeholders(!isEditingStakeholders)}
                      />
                    )}
                  </div>
                  {!isEditingStakeholders ? (
                    stakeholders.length > 0 ? (
                      <div className='flex flex-col gap-3 p-4 bg-gray-50 rounded-lg'>
                        {stakeholders.map((stakeholder) => (
                          <div
                            key={stakeholder.tenantStakeholderId}
                            className='pb-3 border-b border-gray-200 last:border-0 last:pb-0'
                          >
                            <div>
                              <span className='text-xs text-light/60'>Name:</span>
                              <p className='text-sm text-light'>
                                {stakeholder.firstName} {stakeholder.lastName}
                              </p>
                            </div>
                            <div>
                              <span className='text-xs text-light/60'>Email:</span>
                              <p className='text-sm text-light'>{stakeholder.email}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className='flex flex-col gap-3 p-4 bg-gray-50 rounded-lg'>
                        <p className='text-sm text-light/60 text-center'>
                          No stakeholders added yet
                        </p>
                        <button
                          type='button'
                          onClick={() => setIsEditingStakeholders(true)}
                          className='px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90'
                        >
                          Add stakeholder
                        </button>
                      </div>
                    )
                  ) : (
                    <div className='flex flex-col gap-4 p-4 bg-gray-50 rounded-lg'>
                      {/* Existing Stakeholders List */}
                      {stakeholders.length > 0 && (
                        <div className='space-y-3'>
                          <div className='text-sm font-medium text-light'>
                            Existing stakeholders
                          </div>
                          {stakeholders.map((stakeholder) => (
                            <div key={stakeholder.tenantStakeholderId}>
                              {editingStakeholderId === stakeholder.tenantStakeholderId ? (
                                <div className='p-3 bg-white border border-primary rounded-lg space-y-3'>
                                  <div className='text-sm font-medium text-light mb-2'>
                                    Edit stakeholder
                                  </div>
                                  <form
                                    onSubmit={stakeholderFormik.handleSubmit}
                                    className='space-y-3'
                                  >
                                    <div className='grid grid-cols-2 gap-3'>
                                      <div className={'inputWrapper'}>
                                        <div className={'label-light'}>First name</div>
                                        <div className={'inputField'}>
                                          <input
                                            name='firstName'
                                            placeholder='First Name'
                                            autoComplete='off'
                                            value={stakeholderFormik.values.firstName}
                                            onChange={stakeholderFormik.handleChange}
                                            onBlur={stakeholderFormik.handleBlur}
                                            onKeyDown={(event) => {
                                              if (event.key === 'Enter') {
                                                event.preventDefault();
                                              }
                                            }}
                                          />
                                        </div>
                                        {stakeholderFormik.touched.firstName &&
                                          stakeholderFormik.errors.firstName && (
                                            <div className='error'>
                                              {stakeholderFormik.errors.firstName}
                                            </div>
                                          )}
                                      </div>
                                      <div className={'inputWrapper'}>
                                        <div className={'label-light'}>Last name</div>
                                        <div className={'inputField'}>
                                          <input
                                            name='lastName'
                                            placeholder='Last Name'
                                            autoComplete='off'
                                            value={stakeholderFormik.values.lastName}
                                            onChange={stakeholderFormik.handleChange}
                                            onBlur={stakeholderFormik.handleBlur}
                                            onKeyDown={(event) => {
                                              if (event.key === 'Enter') {
                                                event.preventDefault();
                                              }
                                            }}
                                          />
                                        </div>
                                        {stakeholderFormik.touched.lastName &&
                                          stakeholderFormik.errors.lastName && (
                                            <div className='error'>
                                              {stakeholderFormik.errors.lastName}
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                    <div className={'inputWrapper'}>
                                      <div className={'label-light'}>Email</div>
                                      <div className={'inputField'}>
                                        <input
                                          name='email'
                                          type='email'
                                          placeholder='Email Address'
                                          autoComplete='off'
                                          value={stakeholderFormik.values.email}
                                          onChange={stakeholderFormik.handleChange}
                                          onBlur={stakeholderFormik.handleBlur}
                                          onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                              event.preventDefault();
                                            }
                                          }}
                                        />
                                      </div>
                                      {stakeholderFormik.touched.email &&
                                        stakeholderFormik.errors.email && (
                                          <div className='error'>
                                            {stakeholderFormik.errors.email}
                                          </div>
                                        )}
                                    </div>
                                    <div className='flex gap-2'>
                                      <button
                                        type='button'
                                        onClick={handleCancelEdit}
                                        className='flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100'
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        type='submit'
                                        className='flex-1 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50'
                                        disabled={
                                          stakeholderFormik.isSubmitting ||
                                          !stakeholderFormik.isValid
                                        }
                                      >
                                        {stakeholderFormik.isSubmitting ? 'Saving...' : 'Save'}
                                      </button>
                                    </div>
                                  </form>
                                </div>
                              ) : (
                                <div className='flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg'>
                                  <div className='flex-1'>
                                    <p className='text-sm font-medium text-light'>
                                      {stakeholder.firstName} {stakeholder.lastName}
                                    </p>
                                    <p className='text-xs text-light/60'>{stakeholder.email}</p>
                                  </div>
                                  <div className='flex gap-2'>
                                    <button
                                      type='button'
                                      onClick={() => handleEditStakeholder(stakeholder)}
                                      className='px-3 py-1 text-xs text-primary hover:text-primary/80 border border-primary rounded hover:bg-primary/10'
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type='button'
                                      onClick={() =>
                                        handleDeleteStakeholder(stakeholder.tenantStakeholderId)
                                      }
                                      className='px-3 py-1 text-xs text-red-600 hover:text-red-700 border border-red-300 rounded hover:bg-red-50'
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add New Stakeholder Form */}
                      {!editingStakeholderId && (
                        <div className='pt-4 border-t border-gray-200'>
                          <div className='text-sm font-medium text-light mb-3'>
                            Add new stakeholder
                          </div>
                          <form onSubmit={stakeholderFormik.handleSubmit} className='space-y-3'>
                            <div className='grid grid-cols-2 gap-3'>
                              <div className={'inputWrapper'}>
                                <div className={'label-light'}>First name</div>
                                <div className={'inputField'}>
                                  <input
                                    name='firstName'
                                    placeholder='First Name'
                                    autoComplete='off'
                                    value={stakeholderFormik.values.firstName}
                                    onChange={stakeholderFormik.handleChange}
                                    onBlur={stakeholderFormik.handleBlur}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        event.preventDefault();
                                      }
                                    }}
                                  />
                                </div>
                                {stakeholderFormik.touched.firstName &&
                                  stakeholderFormik.errors.firstName && (
                                    <div className='error'>
                                      {stakeholderFormik.errors.firstName}
                                    </div>
                                  )}
                              </div>
                              <div className={'inputWrapper'}>
                                <div className={'label-light'}>Last name</div>
                                <div className={'inputField'}>
                                  <input
                                    name='lastName'
                                    placeholder='Last Name'
                                    autoComplete='off'
                                    value={stakeholderFormik.values.lastName}
                                    onChange={stakeholderFormik.handleChange}
                                    onBlur={stakeholderFormik.handleBlur}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        event.preventDefault();
                                      }
                                    }}
                                  />
                                </div>
                                {stakeholderFormik.touched.lastName &&
                                  stakeholderFormik.errors.lastName && (
                                    <div className='error'>{stakeholderFormik.errors.lastName}</div>
                                  )}
                              </div>
                            </div>
                            <div className={'inputWrapper'}>
                              <div className={'label-light'}>Email</div>
                              <div className={'inputField'}>
                                <input
                                  name='email'
                                  type='email'
                                  placeholder='Email Address'
                                  autoComplete='off'
                                  value={stakeholderFormik.values.email}
                                  onChange={stakeholderFormik.handleChange}
                                  onBlur={stakeholderFormik.handleBlur}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                      event.preventDefault();
                                    }
                                  }}
                                />
                              </div>
                              {stakeholderFormik.touched.email &&
                                stakeholderFormik.errors.email && (
                                  <div className='error'>{stakeholderFormik.errors.email}</div>
                                )}
                            </div>
                            <button
                              type='submit'
                              className='w-full px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50'
                              disabled={
                                stakeholderFormik.isSubmitting || !stakeholderFormik.isValid
                              }
                            >
                              {stakeholderFormik.isSubmitting ? 'Adding...' : 'Add stakeholder'}
                            </button>
                          </form>
                        </div>
                      )}

                      <div className='flex gap-2 pt-2'>
                        <button
                          type='button'
                          className='flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100'
                          onClick={() => {
                            setIsEditingStakeholders(false);
                            setEditingStakeholderId(null);
                            stakeholderFormik.resetForm();
                          }}
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className='w-full mb-10'>
                <button
                  className={'submitBtn'}
                  onClick={() => formik.handleSubmit()}
                  disabled={formik.isSubmitting || !formik.isValid}
                  type='submit'
                >
                  {formik.isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        );
      case 'upload-photo':
        return (
          <>
            <div className='flex gap-5 px-6 py-6'>
              <ArrowLeft
                onClick={() => setCurrentView('profile')}
                className='cursor-pointer text-primary'
              />
            </div>
            <div className='flex flex-col items-center h-full px-12'>
              <UploadPhoto showClose={false} />
            </div>
          </>
        );
      case 'update-phone':
        return (
          <>
            <div className='flex gap-5 px-6 py-6'>
              <ArrowLeft
                onClick={() => setCurrentView('profile')}
                className='cursor-pointer text-primary'
              />
            </div>
            <div className='flex flex-col items-center h-full px-12'>
              <UpdatePhone
                phoneNumber={phoneNumber}
                onSuccess={(phone) => {
                  setPhoneNumber(phone);
                  setCurrentView('verify-phone-code');
                }}
              />
            </div>
          </>
        );
      case 'verify-phone-code':
        return (
          <>
            <div className='flex gap-5 px-6 py-6'>
              <ArrowLeft
                onClick={() => setCurrentView('profile')}
                className='cursor-pointer text-primary'
              />
            </div>
            <div className='flex flex-col items-center h-full px-12'>
              <VerifyPhoneCode
                phoneNumber={phoneNumber}
                mode='verify'
                onSuccess={() => void handlePhoneVerifySuccess()}
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };
  const registrationSchema = Yup.object().shape({
    firstname: Yup.string()
      .min(2, 'Minimum 2 characters')
      .max(50, 'Maximum 50 characters')
      .matches(/^[a-zA-Z0-9 ]*$/, 'First Name must only include letters and numbers')
      .optional(),
    lastname: Yup.string()
      .min(1, 'Minimum 1 character')
      .max(50, 'Maximum 50 characters')
      .matches(/^[a-zA-Z0-9 ]*$/, 'Last Name must only include letters and numbers')
      .optional(),
    middlename: Yup.string()
      .trim()
      .min(1, 'Minimum 1 character')
      .max(10, 'Maximum 10 characters')
      .matches(/^[A-Za-z]+(?: [A-Za-z]+)*$/, 'Middle name must only include letters')
      .optional(),

    occupation: occupationSchema,
  });

  const companySchema = Yup.object().shape({
    companyName: Yup.string().required('Company Legal Name is required'),
    companyDisplayName: Yup.string().optional(),
    companyKycBusinessTypeId: Yup.string().required('Business Type is required'),
  });

  const getStakeholderSchema = (editingId?: string | null) => {
    return Yup.object().shape({
      firstName: Yup.string().trim().required('First Name is required'),
      lastName: Yup.string().trim().required('Last Name is required'),
      email: Yup.string()
        .trim()
        .email('Invalid email address')
        .required('Email is required')
        .test('unique-email', 'This email is already added', (value) => {
          if (!value) return true;
          // Exclude the current stakeholder being edited from the uniqueness check
          return !stakeholders.some(
            (s) =>
              s.email.toLowerCase() === value.toLowerCase().trim() &&
              s.tenantStakeholderId !== editingId
          );
        }),
    });
  };
  const initialValues = {
    firstname: '',
    lastname: '',
    middlename: '',
    dob: '',
    occupation: '',
    phoneNumber: '',
  };
  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      const updatedData = {
        userDetails: {
          firstName: values.firstname,
          middleName: values.middlename || '',
          lastName: values.lastname,
          dateOfBirth: values?.dob ? standardizeDate(values?.dob) : '',
        },
        tenantDetails: {
          occupation: values.occupation,
        },
      };

      try {
        await updatePersonalInfo(auth?.data.token, updatedData);
        setUserProfile({ firstName: values.firstname, lastName: values.lastname });
        showToast({ message: 'Profile Updated Successfully!', type: 'success' });

        setModal(null);
      } catch (error) {
        if (error) {
          const errorMessage =
            (error as { message?: string })?.message || 'An unknown error occurred';
          showToast({ message: `Error! ${errorMessage}`, type: 'error' });
        }
        setStatus('Please fill all required fields');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const companyFormik = useFormik({
    initialValues: {
      companyName: companyDetails?.name || '',
      companyDisplayName: companyDetails?.displayName || '',
      companyKycBusinessTypeId: companyDetails?.kycBusinessTypeId || '',
    },
    enableReinitialize: true,
    validationSchema: companySchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const requestBody = {
          name: values.companyName,
          displayName: values.companyDisplayName || undefined,
          kycBusinessTypeId: values.companyKycBusinessTypeId,
        };

        await addCompanyInformation(auth?.data.token, requestBody);
        showToast({ message: 'Company Information Updated Successfully!', type: 'success' });

        // Refresh company details and stakeholders
        const response = await getTenantCompany(auth?.data.token);
        setCompanyDetails(response.data);
        await fetchStakeholders();
        setIsEditingCompany(false);
      } catch (error) {
        const errorMessage =
          (error as { message?: string })?.message || 'An unknown error occurred';
        showToast({ message: `Error! ${errorMessage}`, type: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const stakeholderFormik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
    },
    enableReinitialize: true,
    validationSchema: getStakeholderSchema(editingStakeholderId),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        if (editingStakeholderId) {
          await updateStakeholder(auth?.data.token, editingStakeholderId, values);
          showToast({ message: 'Stakeholder Updated Successfully!', type: 'success' });
          setEditingStakeholderId(null);
        } else {
          await addStakeholder(auth?.data.token, values);
          showToast({ message: 'Stakeholder Added Successfully!', type: 'success' });
        }
        await fetchStakeholders();
        resetForm();
      } catch (error) {
        const errorMessage =
          (error as { message?: string })?.message || 'An unknown error occurred';
        showToast({ message: `Error! ${errorMessage}`, type: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleEditStakeholder = (stakeholder: Stakeholder) => {
    setEditingStakeholderId(stakeholder.tenantStakeholderId);
    stakeholderFormik.setValues({
      firstName: stakeholder.firstName,
      lastName: stakeholder.lastName,
      email: stakeholder.email,
    });
  };

  const handleCancelEdit = () => {
    setEditingStakeholderId(null);
    stakeholderFormik.resetForm();
  };

  const handleDeleteStakeholder = async (id: string) => {
    try {
      await deleteStakeholder(auth?.data.token, id);
      showToast({ message: 'Stakeholder Deleted Successfully!', type: 'success' });
      await fetchStakeholders();
    } catch (error) {
      const errorMessage = (error as { message?: string })?.message || 'An unknown error occurred';
      showToast({ message: `Error! ${errorMessage}`, type: 'error' });
    }
  };

  useEffect(() => {
    const formattedDate = userData?.user?.dateOfBirth
      ? formatDateToDisplayUTC(userData?.user?.dateOfBirth)
      : '';
    setPhoneNumber(userData?.user?.phoneNumber || '');
    setStartDate(userData?.user?.dateOfBirth ? parseDateStringToDate(formattedDate) : null);
    formik.setFieldValue('firstname', userData?.user?.firstName || '');
    formik.setFieldValue('lastname', userData?.user?.lastName || '');
    formik.setFieldValue('middlename', userData?.user?.middleName || '');
    formik.setFieldValue('occupation', userData?.tenant?.occupation || '');
    formik.setFieldValue('dob', formattedDate);
    formik.setFieldValue('phoneNumber', userData?.user?.phoneNumber || '');
  }, [userData]);

  const fetchTenantDetails = async () => {
    try {
      const response = await getTenantDetails(auth?.data.token);

      setUserData(response.data);
    } catch (error) {
      console.error('Error fetching tenant details:', error);
    }
  };

  const handlePhoneVerifySuccess = async () => {
    if (auth?.data.token) {
      try {
        const { data } = await getUserByToken(auth.data.token);
        setCurrentUser(data);
      } catch {
        // fetchTenantDetails will still refresh profile state.
      }
    }
    await fetchTenantDetails();
    showToast({ message: 'Phone number updated successfully', type: 'success' });
    setCurrentView('profile');
  };
  useEffect(() => {
    fetchTenantDetails();
  }, []);

  const fetchCompanyDetails = async () => {
    if (userData?.tenant?.tenantCompanyId && auth?.data.token) {
      try {
        const response = await getTenantCompany(auth?.data.token);
        setCompanyDetails(response.data);
      } catch (error) {
        console.error('Error fetching company details:', error);
      }
    }
  };

  const fetchKYCBusinessTypes = async () => {
    if (auth?.data.token) {
      try {
        const response = await getKYCBusinessTypes(auth?.data.token);
        setKycBusinessTypes(response);
      } catch (error) {
        console.error('Error fetching KYC business types:', error);
      }
    }
  };

  const fetchStakeholders = async () => {
    if (userData?.tenant?.tenantCompanyId && auth?.data.token) {
      try {
        const response = await getStakeholders(auth?.data.token);
        if (response?.data && response.data.length > 0) {
          setStakeholders(response.data);
        } else {
          setStakeholders([]);
        }
      } catch (error) {
        console.error('Error fetching stakeholders:', error);
        setStakeholders([]);
      }
    }
  };

  useEffect(() => {
    fetchCompanyDetails();
    fetchStakeholders();
  }, [userData?.tenant?.tenantCompanyId]);

  useEffect(() => {
    if (isEditingCompany) {
      fetchKYCBusinessTypes();
    }
  }, [isEditingCompany]);
  const slideVariants = {
    enter: {
      y: -20,
      opacity: 0,
    },
    center: {
      y: 0,
      opacity: 1,
    },
    exit: {
      y: 20,
      opacity: 0,
      scale: 0.98, // slight shrink to enhance morph feel
    },
  };

  return (
    <motion.div
      className='relative w-[554px]  bg-[#FFF] rounded-[12px] flex flex-col overflow-y-auto max-h-[90vh] max-w-[90vw]'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {currentview === 'profile' && (
        <div className='flex justify-end p-4'>
          <X
            className='cursor-pointer text-gray-500 hover:text-gray-700'
            size={24}
            onClick={() => setModal(null)}
          />
        </div>
      )}

      <div className='flex-1 flex items-center justify-center'>
        <AnimatePresence mode='wait' custom={currentview}>
          <motion.div
            key={currentview}
            custom={currentview}
            variants={slideVariants}
            initial='enter'
            animate='center'
            exit='exit'
            transition={{ duration: 0.3 }}
            className='w-full'
          >
            {getView(currentview)}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export { Profile };
